import React, { Key, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './Blog.scss'
import Swal from 'sweetalert2'

import { Controller, useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { JSONContent } from '@tiptap/core'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import ArticleEditor from 'Pages/Frontend/Components/Blog/ArticleEditor/ArticleEditor'
import DragAndDropIcon from 'Assets/Icons/upload-icon-gray.png'
import BlogPreviewModal from '../../Components/Blog/BlogPreviewModal'
import BlogConflictModal from '../../Components/Blog/BlogConflictModal'
import BlogRecoveryBanner from '../../Components/Blog/BlogRecoveryBanner'
import blogService from 'Services/blogService'
import { DEFAULT_EDITOR_CAPABILITIES } from 'Adapters/TipTapAdapter'
import { blogResponseToForm } from 'Adapters/BlogResponseAdapter'
import { useBlogPreview } from 'Hooks/Blog/useBlogPreview'
import { useBlogAutosave } from 'Hooks/Blog/useBlogAutosave'
import { EMPTY_FORM_VALUES, pickArticleValues } from 'Helpers/Blog/blogForm'
import { BlogFormValues, BlogValidationMode, createBlogResolver } from 'Helpers/Blog/blogSchemas'
import { handleError, handleSuccess } from 'Helpers'
import { ToastContainer } from 'react-toastify'

export const CATEGORY_OPTIONS = [
  { value: 'energy access', label: 'Energy Access' },
  { value: 'financing', label: 'Financing' },
  { value: 'solar & power', label: 'Solar & Power' },
  { value: 'company news', label: 'Company News' },
  { value: 'market trends', label: 'Market Trends' },
]

const getErrorMessage = (error: unknown, fallback: string): string => {
  const e = error as {
    response?: { data?: { message?: string | string[]; error?: string } }
    message?: string
  }
  const message = e.response?.data?.message
  if (Array.isArray(message)) return message.join(' ')
  return message || e.response?.data?.error || e.message || fallback
}

function Blog() {
  const { slug: routeSlug } = useParams<{ slug?: string }>()
  const navigate = useNavigate()

  const [isPublishing, setIsPublishing] = useState(false)

  // draft vs publish validation share one form; the mode is switched per action.
  const validationModeRef = useRef<BlogValidationMode>('draft')
  const resolver = useMemo(() => createBlogResolver(() => validationModeRef.current), [])

  const {
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    trigger,
    watch,
  } = useForm<BlogFormValues>({
    resolver,
    defaultValues: EMPTY_FORM_VALUES,
  })

  // load state for editing an existing article
  const [loadStatus, setLoadStatus] = useState<'loading' | 'ready' | 'error'>(
    routeSlug ? 'loading' : 'ready',
  )
  const [loadError, setLoadError] = useState<string | null>(null)
  const [droppedBlockTypes, setDroppedBlockTypes] = useState<string[]>([])
  const [acknowledgedDropped, setAcknowledgedDropped] = useState(false)

  const watched = watch()
  const articleValues = useMemo(() => pickArticleValues(watched), [watched])

  const author: string | undefined = undefined

  const autosave = useBlogAutosave({
    existingSlug: routeSlug ?? null,
    values: articleValues,
    author,
    // Don't save while the article is loading, or while unsupported blocks haven't been acknowledged.
    paused: loadStatus !== 'ready' || (droppedBlockTypes.length > 0 && !acknowledgedDropped),
    onError: (message) => {
      handleError(message)
    },
    onSuccess: (message) => {
      handleSuccess(message)
    },
  })
  const autosaveRef = useRef(autosave)
  autosaveRef.current = autosave

  // const preview = useBlogPreview({
  //   ensureSaved: autosave.ensureSaved,
  //   onError: (message) => {
  //     handleError(message)
  //   },
  // })

  const articleStatus = autosave.editorState.publishedStatus ?? 'draft'
  const isPublished = articleStatus === 'published'

  // Load an existing article, then offer any unsaved local work. Never auto-applied.
  useEffect(() => {
    if (!routeSlug) {
      autosaveRef.current.recovery.checkForRecovery(null)
      return undefined
    }

    let cancelled = false
    setLoadStatus('loading')

    blogService
      .getSingle(routeSlug)
      .then((res) => {
        console.log('res', res)
        if (cancelled) return
        const {
          values,
          editorState,
          droppedBlockTypes: dropped,
        } = blogResponseToForm(res.data.data, DEFAULT_EDITOR_CAPABILITIES)
        reset({ ...values, visibility: '' })
        autosaveRef.current.hydrate(values, editorState)
        setDroppedBlockTypes(dropped)
        setAcknowledgedDropped(false)
        autosaveRef.current.recovery.checkForRecovery(values)
        setLoadStatus('ready')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const status = (error as { response?: { status?: number } }).response?.status
        setLoadError(
          status === 404
            ? 'This article could not be found.'
            : getErrorMessage(error, 'Unable to load this article.'),
        )
        setLoadStatus('error')
        handleError('Unable to load article')
      })

    return () => {
      cancelled = true
    }
  }, [routeSlug, reset])

  // Warn before closing the tab with unsaved/in-flight work
  const [isUploading, setIsUploading] = useState(false)
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (autosaveRef.current.getIsDirty() || autosaveRef.current.getIsSaving() || isUploading) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isUploading])

  // Cover image upload
  const [progress, setProgress] = useState(0)
  const [coverPreview, setCoverPreview] = useState<string | null>(null)
  const [coverError, setCoverError] = useState<string | null>(null)

  const coverFileRef = useRef<HTMLInputElement | null>(null)

  const coverImage = watch('coverImage')
  const category = watch('category')

  useEffect(() => {
    return () => {
      if (coverPreview) URL.revokeObjectURL(coverPreview)
    }
  }, [coverPreview])

  const handleCoverFile = async (file?: File) => {
    if (!file || isUploading) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']

    if (!allowedTypes.includes(file.type)) {
      setCoverError('Only JPG, PNG or WebP images are allowed')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setCoverError('Image must not exceed 5 MB')
      return
    }

    const previewUrl = URL.createObjectURL(file)

    setCoverPreview((previous) => {
      if (previous) URL.revokeObjectURL(previous)
      return previewUrl
    })

    setCoverError(null)
    await uploadCoverImage(file)
  }

  const uploadCoverImage = async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      setIsUploading(true)
      setProgress(0)

      const res = await blogService.file(formData, {
        onUploadProgress: (event) => {
          const total = event.total || file.size
          setProgress(Math.round((event.loaded * 100) / total))
        },
      })
      console.log('res', res)

      // The form holds the uploaded image metadata, never the browser File.
      setValue('coverImage', res.data.data, { shouldValidate: true, shouldDirty: true })
    } catch (error) {
      console.error('Cover image upload failed:', error)
      setCoverError(getErrorMessage(error, 'Failed to upload cover image. Please try again.'))
      // Fall back to whatever cover the article already had.
      setCoverPreview(null)
    } finally {
      setIsUploading(false)
    }
  }

  // removing a cover sends coverImage: null, which also clears its alt text server-side.
  const removeCover = () => {
    setCoverPreview(null)
    setCoverError(null)
    setValue('coverImage', null, { shouldValidate: true, shouldDirty: true })
    setValue('coverImageAltText', '', { shouldDirty: true })
  }

  const handleCoverDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    handleCoverFile(e.dataTransfer.files[0])
  }

  const [tagInput, setTagInput] = useState('')

  const isBusy =
    isPublishing ||
    isUploading ||
    autosave.saveStatus.state === 'saving' ||
    Boolean(autosave.conflict)

  /** Save Draft (or "Save changes" on a published article, which is live and so uses the stricter publish rules). */
  const handleSaveDraft = async () => {
    validationModeRef.current = isPublished ? 'publish' : 'draft'
    const valid = await trigger()
    validationModeRef.current = 'draft'
    if (!valid) return

    const saved = await autosave.saveNow()
    if (saved) {
      handleSuccess(isPublished ? 'Changes saved' : 'Draft saved')
    }
  }

  const handlePublish = async () => {
    if (isPublishing) return

    validationModeRef.current = 'publish'
    const valid = await trigger()
    validationModeRef.current = 'draft'
    if (!valid) {
      handleError('Please fix the highlighted fields before publishing')
      return
    }
    setIsPublishing(true)
    try {
      // Saves pending edits and flips the status in one request, using the latest confirmed version.
      await autosave.publish()
    } finally {
      setIsPublishing(false)
    }
  }

  const handleUnpublish = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Unpublish this article?',
      text: 'It will disappear from the public site until you publish it again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Unpublish',
    })
    if (!isConfirmed) return

    setIsPublishing(true)
    try {
      await autosave.unpublish()
    } finally {
      setIsPublishing(false)
    }
  }

  // const handlePreview = async () => {
  //   // Previewing saves first, and edits to a published article are live as soon as they are saved.
  //   if (isPublished && autosave.getIsDirty()) {
  //     const { isConfirmed } = await Swal.fire({
  //       title: 'Save and preview?',
  //       text: 'This article is published. Previewing saves your changes, and they go live immediately.',
  //       icon: 'warning',
  //       showCancelButton: true,
  //       confirmButtonText: 'Save & preview',
  //     })
  //     if (!isConfirmed) return
  //   }
  //   await preview.open()
  // }

  const handleCancel = async () => {
    if (autosave.getIsDirty()) {
      const { isConfirmed } = await Swal.fire({
        title: 'Leave without saving?',
        text: 'Your changes are stored on this device and will be offered back the next time you open this article.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Leave',
      })
      if (!isConfirmed) return
    }
    navigate(-1)
  }

  // Recovery + conflict handlers
  const handleRestoreSnapshot = async () => {
    const snapshot = autosave.recovery.recoverableSnapshot
    if (!snapshot) return

    const serverVersion = autosave.editorState.version
    if (
      serverVersion !== null &&
      snapshot.baseVersion !== null &&
      serverVersion > snapshot.baseVersion
    ) {
      const { isConfirmed } = await Swal.fire({
        title: 'Restore older local copy?',
        text: 'This article was updated after your local copy was made. Saving the restored copy will overwrite those newer changes.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Restore anyway',
      })
      if (!isConfirmed) return
    }

    const taken = autosave.recovery.takeSnapshot()
    if (taken) reset({ ...taken.values, visibility: '' })
  }

  const handleDiscardSnapshot = async () => {
    const { isConfirmed } = await Swal.fire({
      title: 'Delete the local copy?',
      text: 'The unsaved changes stored on this device will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    })
    if (isConfirmed) autosave.recovery.discard()
  }

  const applyServerResult = useCallback(
    (result: ReturnType<typeof autosave.reloadLatest>) => {
      if (!result) return
      reset({ ...result.values, visibility: '' })
      setDroppedBlockTypes(result.droppedBlockTypes)
      setAcknowledgedDropped(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reset],
  )

  // Inline save indicator
  const renderSaveStatus = (): { text: string; modifier: string } | null => {
    const status = autosave.saveStatus
    switch (status.state) {
      case 'unsaved':
        return {
          text: isPublished
            ? 'Unsaved changes. Click Save changes to publish them.'
            : 'Unsaved changes',
          modifier: '',
        }
      case 'saving':
        return { text: 'Saving changes...', modifier: '' }
      case 'saved':
        return {
          text: `Saved at ${new Date(status.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
          modifier: '',
        }
      case 'error':
        return { text: status.message, modifier: 'Blog__save-status--error' }
      case 'conflict':
        return {
          text: 'Conflict detected. Review changes before saving.',
          modifier: 'Blog__save-status--conflict',
        }
      default:
        return null
    }
  }

  const saveIndicator = renderSaveStatus()

  const categoryOptions = useMemo(
    () =>
      !category || CATEGORY_OPTIONS.some((option) => option.value === category)
        ? CATEGORY_OPTIONS
        : [...CATEGORY_OPTIONS, { value: category, label: category }],
    [category],
  )

  if (loadStatus !== 'ready') {
    return (
      <div className='Blog'>
        <div className='container mt-4'>
          {loadStatus === 'loading' ? (
            <div role='status'>
              <span className='spinner-border spinner-border-sm' aria-hidden='true' /> Loading
              article…
            </div>
          ) : (
            <div className='alert alert-danger' role='alert'>
              {loadError}{' '}
              <button
                type='button'
                className='btn btn-link p-0'
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className='Blog'>
      <div className='container mt-4'>
        <div className='container'>
          <div className='card-header border-0 px-0'>
            <div className='row d-flex align-items-end'>
              <div className='col-6 col-md-6 my-auto'>
                <div className='Blog__title'>
                  {autosave.editorState.slug ? 'Edit Post' : 'Create New Post'}
                </div>
              </div>
              <div className='col-6 col-md-6 d-flex justify-content-end'>
                <div className='Blog__action-bar' role='region' aria-label='Publish controls'>
                  <div className='Blog__action-bar__buttons'>
                    <button
                      type='button'
                      className='Blog__action-bar__btn'
                      disabled={isPublishing}
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    {/* Preview, styled like the other buttons */}
                    {/* <button
                      type='button'
                      className='Blog__action-bar__btn'
                      disabled={isBusy}
                      onClick={handlePreview}
                    >
                      Preview
                    </button> */}
                    <button
                      type='button'
                      className='Blog__action-bar__btn'
                      disabled={isBusy}
                      onClick={handleSaveDraft}
                    >
                      {isPublished ? 'Save changes' : 'Save draft'}
                    </button>
                    <button
                      type='button'
                      className='Blog__action-bar__btn Blog__action-bar__btn--pink'
                      disabled={isBusy || isPublished}
                      onClick={handlePublish}
                    >
                      {isPublishing && (
                        <span
                          className='spinner-border spinner-border-sm Blog__action-bar__spinner'
                          aria-hidden='true'
                        />
                      )}
                      {isPublishing ? 'Publishing…' : 'Publish'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {saveIndicator && (
              <span
                className={`Blog__save-status ${saveIndicator.modifier}`}
                role='status'
                aria-live='polite'
              >
                {saveIndicator.text}
              </span>
            )}
          </div>

          {/* unsaved local work found, offered back (not automatically appllied) */}
          {autosave.recovery.recoverableSnapshot && (
            <BlogRecoveryBanner
              snapshot={autosave.recovery.recoverableSnapshot}
              serverVersion={autosave.editorState.version}
              onRestore={handleRestoreSnapshot}
              onNotNow={autosave.recovery.dismissPrompt}
              onDiscard={handleDiscardSnapshot}
            />
          )}

          {/* blocks this editor can't display would be deleted by a save */}
          {droppedBlockTypes.length > 0 && !acknowledgedDropped && (
            <div className='alert alert-warning mt-3' role='alert'>
              <strong>This article has content this editor can&apos;t display</strong> (
              {droppedBlockTypes.join(', ')}). Saving from here would remove it, so saving is
              paused.{' '}
              <button
                type='button'
                className='btn btn-link p-0 me-3'
                onClick={() => setAcknowledgedDropped(true)}
              >
                Continue and remove it when saving
              </button>
              <button type='button' className='btn btn-link p-0' onClick={() => navigate(-1)}>
                Go back
              </button>
            </div>
          )}

          <div className='row g-3 mt- Blog__layout'>
            <div className='col-md-8 Blog__main'>
              <div className='card card-white border-0 h-100 Blog__main-card'>
                <div className='col-12 Blog__main-content'>
                  <div className='form-input Blog__field'>
                    <label htmlFor='title'>Title *</label>
                    <input
                      id='title'
                      type='text'
                      className='form-control Blog__input'
                      {...register('title')}
                      placeholder='Add a post title'
                    />
                    {errors.title && (
                      <div className='Blog__field-error'>{errors.title.message}</div>
                    )}
                  </div>

                  <div className='form-input mt-3 Blog__field'>
                    <label htmlFor=''>Excerpt *</label>
                    <input
                      type='text'
                      className='form-control'
                      {...register('excerpt')}
                      placeholder='One or two sentences shown on the blog listing'
                    />
                    {errors.excerpt && (
                      <div className='Blog__field-error'>{errors.excerpt.message}</div>
                    )}
                  </div>

                  <div className='mt-4'>
                    <Controller
                      name='content'
                      control={control}
                      render={({ field, fieldState }) => (
                        <ArticleEditor
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='col-md-4 d-flex flex-column gap-3 Blog__sidebar'>
              <div className='card card-white border-0 Blog__sidebar-card'>
                <div className='Blog__sidebar-title'>Publishing</div>
                <div className='form-input mt-3 Blog__field'>
                  <label htmlFor=''>Visibility</label>
                  <select
                    {...register('visibility')}
                    id='month-select'
                    className='form-control'
                    value=''
                  >
                    <option disabled value=''>
                      select visibility
                    </option>
                    <option value='true'>Publish immediately</option>
                    <option value='false'>Save draft</option>
                  </select>
                </div>
                <div className='Blog__sidebar-status'>
                  <span>Current status:</span>
                  {isPublished ? (
                    <span className='Blog__sidebar-status--published'>Published</span>
                  ) : (
                    <span className='Blog__sidebar-status--draft'>Draft</span>
                  )}
                  {isPublished && (
                    <button
                      type='button'
                      className='Blog__unpublish'
                      disabled={isBusy}
                      onClick={handleUnpublish}
                    >
                      Unpublish
                    </button>
                  )}
                </div>
              </div>
              <div className='card card-white border-0 Blog__sidebar-card'>
                <div className='Blog__sidebar-title'>Details</div>

                <div className='form-input mt-4 Blog__field'>
                  <label htmlFor='coverImage'>Cover image *</label>
                  <div
                    className={`Blog__cover-upload ${coverError ? 'Blog__cover-upload--error' : ''}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleCoverDrop}
                    onClick={() => coverFileRef.current?.click()}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        coverFileRef.current?.click()
                      }
                    }}
                  >
                    <input
                      ref={coverFileRef}
                      id='coverImage'
                      type='file'
                      accept='.jpg,.jpeg,.png,.webp'
                      className='file_input_hidden'
                      onChange={(e) => {
                        handleCoverFile(e.target.files?.[0])
                        e.target.value = ''
                      }}
                    />

                    {coverPreview || coverImage?.url ? (
                      <div className='Blog__cover-preview'>
                        <img src={coverPreview || coverImage?.url} alt='Cover image preview' />
                      </div>
                    ) : (
                      <div className='Blog__cover-placeholder'>
                        <img src={DragAndDropIcon} alt='' />
                        <span className='Blog__cover-placeholder--title'>
                          Click to upload or drag and drop
                        </span>

                        <div className='Blog__cover-requirements'>
                          <span>JPG, PNG or WebP · Max 5 MB</span>
                          <span>Recommended: 1200 × 630 px</span>
                        </div>
                      </div>
                    )}

                    {isUploading && (
                      <div className='Blog__cover-progress'>
                        <div
                          className='progress-bar'
                          role='progressbar'
                          style={{ width: `${progress}%` }}
                          aria-valuenow={progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          {progress}%
                        </div>
                      </div>
                    )}
                  </div>

                  {coverError && (
                    <div className='text-danger mt-2 Blog__cover-input--error'>{coverError}</div>
                  )}

                  {errors.coverImage && (
                    <div className='text-danger mt-2'>{errors.coverImage.message}</div>
                  )}

                  {coverImage && !isUploading && (
                    <button type='button' className='Blog__cover-remove' onClick={removeCover}>
                      Remove cover image
                    </button>
                  )}
                </div>

                <div className='form-input mt-3 Blog__field'>
                  <label htmlFor='coverImageAltText'>
                    Cover image alt text{coverImage ? ' *' : ''}
                  </label>
                  <input
                    id='coverImageAltText'
                    type='text'
                    className='form-control'
                    {...register('coverImageAltText')}
                    placeholder='Describe the image for screen readers'
                  />
                  {errors.coverImageAltText && (
                    <div className='Blog__field-error'>{errors.coverImageAltText.message}</div>
                  )}
                </div>

                <div className='form-input mt-4 Blog__field'>
                  <label htmlFor='title'>Category *</label>
                  <select {...register('category')} className='form-control' value={category ?? ''}>
                    <option disabled value=''>
                      select category
                    </option>
                    {categoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <div className='Blog__field-error'>{errors.category.message}</div>
                  )}
                </div>
                <div className='form-input mt-3 Blog__field'>
                  <label>Tags</label>

                  <Controller
                    name='tags'
                    control={control}
                    render={({ field, fieldState }) => {
                      const tags = field.value || []
                      const addTag = () => {
                        const newTag = tagInput.trim().replace(/,$/, '').trim()
                        if (!newTag) return

                        const alreadyExists = tags.some(
                          (tag) => tag.toLowerCase() === newTag.toLowerCase(),
                        )

                        if (!alreadyExists) {
                          field.onChange([...tags, newTag])
                        }
                        setTagInput('')
                      }

                      const removeTag = (index: number) => {
                        field.onChange(tags.filter((_, i) => i !== index))
                      }

                      return (
                        <>
                          <div
                            className={`Blog__tags-input ${fieldState.error ? 'Blog__tags-input--error' : ''}`}
                          >
                            <div className='Blog__tags-list'>
                              {tags.map((tag, index) => (
                                <span className='Blog__tag' key={`${tag}-${index}`}>
                                  {tag}

                                  <button
                                    type='button'
                                    className='Blog__tag-remove'
                                    aria-label={`Remove ${tag}`}
                                    onClick={() => removeTag(index)}
                                  >
                                    <span aria-hidden='true'>×</span>
                                  </button>
                                </span>
                              ))}

                              <input
                                type='text'
                                value={tagInput}
                                placeholder={
                                  tags.length ? 'Add another tag...' : 'Type a tag, press Enter'
                                }
                                onChange={(e) => setTagInput(e.target.value)}
                                onBlur={field.onBlur}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ',') {
                                    e.preventDefault()
                                    addTag()
                                  }

                                  if (e.key === 'Backspace' && !tagInput && tags.length) {
                                    removeTag(tags.length - 1)
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {fieldState.error && (
                            <div className='text-danger mt-2'>{fieldState.error.message}</div>
                          )}
                        </>
                      )
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conflict. Autosave stays paused until the admin chooses. */}
      {/* {autosave.conflict && (
        <BlogConflictModal
          latestArticle={autosave.conflict.latestArticle}
          onReloadLatest={() => applyServerResult(autosave.reloadLatest())}
          onRestoreMine={autosave.restoreMine}
          onDiscardMine={() => applyServerResult(autosave.discardMine())}
        />
      )} */}

      {/* Cross-domain preview of the saved draft */}
      {/* {preview.isOpen && (
        <BlogPreviewModal
          status={preview.status}
          errorMessage={preview.errorMessage}
          previewUrl={preview.previewUrl}
          reloadKey={preview.reloadKey}
          iframeRef={preview.iframeRef}
          onClose={preview.close}
          onRefresh={preview.open}
        />
      )} */}
      <ToastContainer />
    </div>
  )
}

export default Blog
